import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import axios, { AxiosResponse } from 'axios';
import LoadingOutlined from '@ant-design/icons/lib/icons/LoadingOutlined';
import { useTranslation } from 'react-i18next';

import { opTime } from 'utils/dates';
import { OpSpin } from 'new-components/DLS/OpSpin/OpSpin';
import { OpModal } from 'new-components/DLS/OpModal/OpModal';
import { useSelectorJs } from 'utils/customHooks';
import { notificationService } from 'containers/AlertWrapper/notificationService';

import {
  VideoDeviceType,
  DevicesByWorkflowType,
} from '../../../../../types/videoDeviceTypes';
import {
  AltaVideoState,
  botLoginAltaVideo,
  botLogoutAltaVideo,
} from '../../../../../store/slices/altaVideoSlice';
import {
  AvaConnection,
  generateUniqueLabel,
} from '../../../../../ava-player/src';
import { DATE_TIME_AM_PM_FORMAT } from '../../../../../constants/dates';
import { VisitsState } from '../../../../../store/slices/visitsSlice';
import { AppDispatch, ImmutableRootState } from '../../../../../store/store';

import DeviceCard from './DeviceCard';

interface ActivityLogVideoModalProps {
  open: boolean;
  onClose: () => void;
  targetType?: 'SIGNIN' | 'SIGNOUT';
  videoDevices: VideoDeviceType[];
  devicesByWorkflow: DevicesByWorkflowType;
  selectedDeviceGuIds: string[];
}

const ActivityLogVideoModal = ({
  open,
  onClose,
  targetType,
  videoDevices,
  devicesByWorkflow,
  selectedDeviceGuIds,
}: ActivityLogVideoModalProps) => {
  const { t } = useTranslation();

  const dispatch: AppDispatch = useDispatch();

  const selectedVisitor = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visits', 'selectedVisitor']),
  ) as VisitsState['selectedVisitor'];
  const altaVideoIntegrationData =
    (useSelectorJs((state: ImmutableRootState) =>
      state.getIn(['altaVideo', 'altaVideoIntegrationData']),
    ) as AltaVideoState['altaVideoIntegrationData']) || [];
  const isLoggedIn = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['altaVideo', 'isLoggedIn']),
  ) as boolean;

  const [selectedDevice, setSelectedDevice] = useState<VideoDeviceType | null>(
    null,
  );
  const [thumbnailUrls, setThumbnailUrls] = useState<Record<string, string>>(
    {},
  );
  const [isThumbnailLoading, setIsThumbnailLoading] = useState(true);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const connRef = useRef<AvaConnection | null>(null);

  const BASE = altaVideoIntegrationData?.[0]?.msiAltaVideoApiHost;

  const timezone = useMemo(
    () =>
      devicesByWorkflow?.location?.timezone
        ? devicesByWorkflow.location.timezone
        : Intl.DateTimeFormat().resolvedOptions().timeZone, // Use system location timezone if timezone is null
    [devicesByWorkflow?.location?.timezone],
  );

  const baseUtc = useMemo(() => {
    const when =
      targetType === 'SIGNOUT'
        ? selectedVisitor?.signOut
        : selectedVisitor?.signIn;
    // signIn/signOut is already UTC — use opTime.utc() to avoid double timezone conversion
    return opTime.utc(when);
  }, [targetType, selectedVisitor?.signIn, selectedVisitor?.signOut]);

  // Clear connection
  const teardown = () => {
    try {
      connRef.current?.teardown();
    } catch {
      // Error during video teardown
    }
    connRef.current = null;
  };

  // Loads thumbnails
  const fetchThumbnails = useCallback(async () => {
    const filteredDevices = videoDevices.filter((d) =>
      selectedDeviceGuIds.includes(d.guId),
    );
    const result: Record<string, string> = {};

    await Promise.all(
      filteredDevices.map(async (device) => {
        if (device.errors?.length) return;

        const sourceIds = Array.isArray(device.sourceId)
          ? device.sourceId
          : [device.sourceId];

        const thumbnail = await sourceIds.reduce<
          Promise<AxiosResponse<Blob> | null>
        >(
          (promiseChain, sourceId) =>
            promiseChain.then((previousResponse) => {
              if (previousResponse) {
                return previousResponse;
              }

              return axios
                .get(
                  `https://${BASE}/api/v1/devices/${device.guId}/sources/${sourceId}/thumbnail`,
                  {
                    params: {
                      time: baseUtc.valueOf(),
                    },
                    withCredentials: true,
                    responseType: 'blob',
                  },
                )
                .catch(() => null);
            }),
          Promise.resolve(null),
        );

        if (thumbnail) {
          result[device.guId] = URL.createObjectURL(thumbnail.data);
        }
      }),
    );
    setThumbnailUrls(result);
    setIsThumbnailLoading(false);

    // Once thumbnail loading is completed, automatically select first available video device
    const firstValidDevice = filteredDevices.find(
      (device) => !device.errors?.length && result[device.guId],
    );
    if (firstValidDevice) {
      setSelectedDevice(firstValidDevice);
    }
  }, [BASE, videoDevices, selectedDeviceGuIds, baseUtc]);

  // Start play (based on the selected device)
  const start = useCallback(async () => {
    if (!selectedDevice) return;
    teardown();

    const element = videoRef.current;
    if (!element) return;

    const label = generateUniqueLabel();

    try {
      // Bot Login
      dispatch(botLoginAltaVideo());

      // TURN
      const { data: turnConfig } = await axios.get(
        `https://${BASE}/api/v1/cloud/turn`,
        { withCredentials: true },
      );

      const ava = new AvaConnection(label, {
        turnConfig,
        videoElement: element,
        onPlaybackError: () => {
          element.pause();
          element.srcObject = null;
          teardown();
          start();
        },
      });

      connRef.current = ava;

      // WEBSOCKET
      const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const wsUrl = `${wsProtocol}://${BASE}/api/v1/devices/${selectedDevice.guId}/dynamicWebrtc`;
      await ava.bootstrap(wsUrl);

      // Play range (±10 seconds)
      const videoStartTime = new Date(baseUtc.valueOf() - 10_000);
      const videoEndTime = new Date(baseUtc.valueOf() + 10_000);

      await ava.startPlayback({
        label,
        deviceId: selectedDevice.guId,
        nodeId: selectedDevice.nodeId,
        startTimestamp: videoStartTime,
        endTimestamp: videoEndTime,
      });
    } catch {
      teardown();
      notificationService.show(
        'error',
        t('visitors.profile.notification.unexpectedVideoError'),
      );
    }
  }, [BASE, selectedDevice, baseUtc, t, dispatch]);

  // Modal open/close: refresh -> bot log in -> load thumbnail
  useEffect(() => {
    if (!open || !selectedVisitor?.id) return;

    teardown();
    setIsThumbnailLoading(true);
    setThumbnailUrls({});
    setSelectedDevice(null);

    dispatch(botLoginAltaVideo());
  }, [BASE, open, selectedVisitor?.id, dispatch, fetchThumbnails, t]);

  // Play when video device is selected
  useEffect(() => {
    if (selectedDevice) {
      start();
    }
  }, [selectedDevice, start]);

  useEffect(() => {
    if (open && isLoggedIn && isThumbnailLoading) {
      fetchThumbnails();
    }
  }, [open, isLoggedIn, isThumbnailLoading, fetchThumbnails]);

  // Cleanup blob URLs when thumbnailUrls changes
  useEffect(
    () => () => {
      Object.values(thumbnailUrls).forEach((url) => {
        if (url?.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    },
    [thumbnailUrls],
  );

  useEffect(
    () => () => {
      teardown();
      dispatch(botLogoutAltaVideo());
    },
    [dispatch],
  );

  return (
    <OpModal
      open={open}
      onCancel={() => {
        setSelectedDevice(null);
        onClose();
      }}
      footer={null}
      width={925}
      destroyOnClose
      title={
        <div style={{ display: 'flex', alignItems: 'baseline' }}>
          {t('location.tabs.video')}
          <span style={{ marginLeft: '8px', fontSize: '13px' }}>
            ({baseUtc.tz(timezone).format(`${DATE_TIME_AM_PM_FORMAT}`)})
          </span>
        </div>
      }
    >
      {isThumbnailLoading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 375,
          }}
        >
          <OpSpin indicator={<LoadingOutlined spin />} />
        </div>
      ) : (
        // Video & Thumbnail Cards
        <div style={{ display: 'flex', maxHeight: '80vh', overflow: 'hidden' }}>
          <div style={{ flex: 1 }}>
            {selectedDevice ? (
              <video
                ref={videoRef}
                muted
                playsInline
                controls
                style={{
                  width: '100%',
                  height: 370,
                  background: '#000000',
                  borderRadius: 15,
                }}
              />
            ) : (
              <div
                style={{
                  width: '100%',
                  height: 375,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {t('visitors.profile.videoLog.modal.selectVideo')}
              </div>
            )}
          </div>

          <div
            style={{
              width: 200,
              paddingLeft: 20,
              overflowY: 'auto',
              maxHeight: '370px',
            }}
          >
            {selectedVisitor?.signIn || selectedVisitor?.signOut ? (
              videoDevices
                .filter((d) => selectedDeviceGuIds.includes(d.guId)) // Filter by selected devices by workflow
                .map((d) => (
                  <DeviceCard
                    key={d.guId}
                    device={d}
                    imageUrl={thumbnailUrls[d.guId] || ''}
                    onSelect={setSelectedDevice}
                  />
                ))
            ) : (
              <div
                style={{
                  padding: 16,
                  textAlign: 'center',
                  color: 'var(--colorTextTertiary)',
                }}
              >
                NOT AVAILABLE
              </div>
            )}
          </div>
        </div>
      )}
    </OpModal>
  );
};

export default ActivityLogVideoModal;
